import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  ShoppingCart,
  Inventory2,
  AccountBalance,
  TrendingUp
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import vendorApi from '../services/vendorApi';

const StatCard = ({ title, value, subtitle, icon, color, onClick }) => (
  <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography color="text.secondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" sx={{ color: color || 'primary.main' }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: color ? `${color}15` : 'primary.light',
            borderRadius: 2,
            p: 1
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const VendorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financials, setFinancials] = useState(null);
  const [sales, setSales] = useState([]);
  const [consignments, setConsignments] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [financialData, salesData, consignmentData] = await Promise.all([
        vendorApi.getFinancials(),
        vendorApi.getSales(),
        vendorApi.getConsignments()
      ]);

      setFinancials(financialData);
      setSales(salesData);
      setConsignments(consignmentData);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const estateSalesStats = financials?.estate_sales || {};
  const consignmentStats = financials?.consignments?.statistics || {};
  const payoutStats = financials?.payouts || {};

  const chartData = sales.slice(0, 6).reverse().map(sale => ({
    name: sale.name?.substring(0, 15) || 'N/A',
    revenue: sale.estimated_value || 0
  }));

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Welcome to your vendor portal
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Sales Revenue"
            value={`$${estateSalesStats.total_revenue?.toLocaleString() || '0'}`}
            subtitle={`${estateSalesStats.total_sales || 0} sales`}
            icon={<ShoppingCart sx={{ color: 'primary.main' }} />}
            color="#1976d2"
            onClick={() => navigate('/vendor/sales')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Commission Owed"
            value={`$${estateSalesStats.total_commission?.toLocaleString() || '0'}`}
            subtitle={`Rate: ${(estateSalesStats.commission_rate || 0.35) * 100}%`}
            icon={<TrendingUp sx={{ color: 'warning.main' }} />}
            color="#ed6c02"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Payout"
            value={`$${payoutStats.pending?.toLocaleString() || '0'}`}
            subtitle={`${payoutStats.count || 0} total payouts`}
            icon={<AccountBalance sx={{ color: 'success.main' }} />}
            color="#2e7d32"
            onClick={() => navigate('/vendor/payouts')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Consignments"
            value={`${consignmentStats.sold || 0}/${consignmentStats.total_items || 0}`}
            subtitle={`${consignmentStats.pending || 0} pending`}
            icon={<Inventory2 sx={{ color: 'info.main' }} />}
            color="#0288d1"
            onClick={() => navigate('/vendor/consignments')}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sales Overview
              </Typography>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  No sales data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/vendor/sales')}
                >
                  View My Sales
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/vendor/consignments')}
                >
                  Track Consignments
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/vendor/payouts')}
                >
                  View Payouts
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/vendor/documents')}
                >
                  My Documents
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Financial Summary
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Net to Vendor
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    ${estateSalesStats.net_to_vendor?.toLocaleString() || '0'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Paid Out
                  </Typography>
                  <Typography variant="h6">
                    ${payoutStats.total_paid?.toLocaleString() || '0'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Sales
              </Typography>
              {sales.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  {sales.slice(0, 5).map((sale) => (
                    <Box
                      key={sale.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => navigate(`/vendor/sales/${sale.id}`)}
                    >
                      <Box>
                        <Typography variant="body1">{sale.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sale.status} • {sale.start_date || 'Not scheduled'}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body1">
                          ${sale.estimated_value?.toLocaleString() || '0'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sale.item_count || 0} items
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  No sales found
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VendorDashboard;
